import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './DispatchEditModal.css';
import { dispatchAPI } from '../../../../config/dispatch-api';
import { API_CONFIG, getApiErrorMessage } from '../../../../config/api';
import { RotateCcw, Copy, MessageCircle, X, ChevronUp, ChevronDown } from 'lucide-react';
import { toast } from 'react-toastify';
import { getMapboxToken, formatDateOnly, normalizeToYYYYMMDD, getBatchDirections } from '../../../../utils/utils';
import { formatPriorityBadgeLabel } from '../../../../utils/priorityDelivery';
import { getOrderTypeBadge } from '../../../../utils/orderConfigHelpers';

const mapboxgl = window.mapboxgl;

const orderTypeBadgeHtml = (type) => {
  const badge = getOrderTypeBadge(type);
  if (!badge) return '';
  const colors = badge.kind === 'return'
    ? { bg: '#ede9fe', fg: '#6d28d9' }
    : { bg: '#e0f2fe', fg: '#0369a1' };
  return `<span style="display:inline-block;margin:4px 4px 4px 0;background:${colors.bg};color:${colors.fg};padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;text-transform:uppercase;">${badge.label}</span>`;
};

// Depot location 
const DEFAULT_DEPOT = [51.4116783, 25.2441101]; // Ecatcho store

// Normalize coordinates from various formats
const normalizeCoord = (stop = {}) => {
  if (stop.coord && typeof stop.coord.lat !== 'undefined' && typeof stop.coord.lon !== 'undefined') {
    return {
      lat: typeof stop.coord.lat === 'string' ? parseFloat(stop.coord.lat) : stop.coord.lat,
      lon: typeof stop.coord.lon === 'string' ? parseFloat(stop.coord.lon) : stop.coord.lon
    };
  }

  if (typeof stop.lat !== 'undefined' && typeof stop.lon !== 'undefined') {
    return {
      lat: typeof stop.lat === 'string' ? parseFloat(stop.lat) : stop.lat,
      lon: typeof stop.lon === 'string' ? parseFloat(stop.lon) : stop.lon
    };
  }

  if (Array.isArray(stop.coordinates) && stop.coordinates.length >= 2) {
    const [lon, lat] = stop.coordinates;
    return {
      lat: typeof lat === 'string' ? parseFloat(lat) : lat,
      lon: typeof lon === 'string' ? parseFloat(lon) : lon
    };
  }

  return { lat: null, lon: null };
};

const toOrderKey = (order) => String(order ?? '');

const buildLockedAssignmentsFromPlans = (plans, override = {}) => {
  const locked = {};
  plans?.forEach((plan) => {
    plan.stops?.forEach((stop) => {
      if (stop?.order != null && stop.order !== '') {
        locked[toOrderKey(stop.order)] = plan.driver;
      }
    });
  });
  Object.entries(override).forEach(([orderId, driver]) => {
    locked[toOrderKey(orderId)] = driver;
  });
  return locked;
};

const collectOrdersFromPlans = (plans, ordersLookup = []) => {
  const collected = [];
  const seen = new Set();

  plans?.forEach((plan) => {
    plan.stops?.forEach((stop) => {
      const key = toOrderKey(stop?.order);
      if (!key || seen.has(key)) return;

      const coord = normalizeCoord(stop);
      if (coord.lat == null || coord.lon == null) return;

      const matched = ordersLookup.find((o) => toOrderKey(o.order) === key);
      seen.add(key);
      collected.push({
        orderId: stop.orderId || matched?.orderId || matched?.id,
        order: stop.order,
        customer: stop.customer || matched?.customer,
        total: stop.total ?? matched?.total,
        coord,
        lat: coord.lat,
        lon: coord.lon,
        deliveryStaffUrl: stop.deliveryStaffUrl || matched?.deliveryStaffUrl || '',
        deliveryDate: stop.deliveryDate || matched?.deliveryDate || '',
        priority_del_time: stop.priority_del_time || matched?.priority_del_time || '',
        type: (() => {
          const matchedType = String(matched?.type || '').toLowerCase();
          if (matchedType === 'return' || matchedType === 'replacement') return matchedType;
          const stopType = String(stop.type || '').toLowerCase();
          if (stopType === 'return' || stopType === 'replacement') return stopType;
          return 'order';
        })(),
        driver: plan.driver,
      });
    });
  });

  return collected;
};

// Build order from stop data
const buildOrderFromStop = (stop = {}, driver = '') => {
  const coord = normalizeCoord(stop);
  return {
    orderId: stop.orderId,
    order: stop.order,
    customer: stop.customer,
    total: stop.total,
    coord,
    lat: coord.lat,
    lon: coord.lon,
    driver,
    eta_arrival: stop.eta_arrival,
    depart_time: stop.depart_time,
    leg_drive_time: stop.leg_drive_time || stop.leg_minutes,
    cumulative_distance: stop.cumulative_distance || stop.cumulative_km,
    handover_time: stop.handover_time,
    deliveryStaffUrl: stop.deliveryStaffUrl || '',
    deliveryDate: stop.deliveryDate || '',
    priority_del_time: stop.priority_del_time || '',
    type: (() => {
      const t = String(stop.type || '').toLowerCase();
      return t === 'return' || t === 'replacement' || t === 'order' ? t : 'order';
    })(),
  };
};

const DispatchEditModal = ({ route, isOpen, onClose, onSave, drivers = [], storeCoordinates = null }) => {
  const getDepotLabel = () => {
    try {
      const adminData = localStorage.getItem('adminData');
      if (adminData) {
        const parsed = JSON.parse(adminData);
        const name = parsed?.currentStore?.storeName || parsed?.storeName;
        if (name) return name;
      }
    } catch (e) {
      console.error('Error parsing adminData for store name:', e);
    }
    return 'Ecatcho Base';
  };
  const DEPOT_LABEL = getDepotLabel();

  const mapContainer = useRef(null);
  const map = useRef(null);
  const assignedMarkersRef = useRef([]);
  const unassignedMarkersRef = useRef([]);

  const [editedRoute, setEditedRoute] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [routeDrivers, setRouteDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [currentPopup, setCurrentPopup] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [lockedAssignments, setLockedAssignments] = useState({});
  const lockedRef = useRef({});
  const [isLoadingUnused, setIsLoadingUnused] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const editedRouteRef = useRef(null);
  const selectedOrdersRef = useRef([]);
  const ordersRef = useRef([]);
  const skipDirectionsRef = useRef(false);
  const routeDriversRef = useRef([]);

  // Use storeCoordinates from props or fallback to default
  const DEPOT = storeCoordinates || DEFAULT_DEPOT;

  // Handle resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize route data when modal opens
  useEffect(() => {
    if (route && route.plans) {
      setEditedRoute(route);
      setLockedAssignments({});
      lockedRef.current = {};

      const driversFromRoute = route.plans.map(plan => plan.driver).filter(Boolean);
      setRouteDrivers(driversFromRoute);

      const routeOrders = [];
      route.plans.forEach(plan => {
        plan.stops?.forEach(stop => {
          const normalized = buildOrderFromStop(stop, plan.driver);
          routeOrders.push(normalized);
        });
      });

      setOrders(routeOrders);
      setSelectedOrders(routeOrders);
      setHasUnsavedChanges(false);
    }
  }, [route]);

  // Keep refs in sync with state
  useEffect(() => {
    editedRouteRef.current = editedRoute;
  }, [editedRoute]);

  useEffect(() => {
    selectedOrdersRef.current = selectedOrders;
  }, [selectedOrders]);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    routeDriversRef.current = routeDrivers;
  }, [routeDrivers]);

  // Get driver color
  const getDriverColor = useCallback((driverName) => {
    const driver = drivers.find(d => d.name === driverName);
    return driver?.color || '#FE8403';
  }, [drivers]);

  // Initialize Mapbox
  useEffect(() => {
    const token = getMapboxToken();
    if (isOpen && mapContainer.current && !map.current && mapboxgl && token) {
      try {
        mapboxgl.accessToken = token;
        map.current = new mapboxgl.Map({
          container: mapContainer.current,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: DEPOT,
          zoom: 11
        });

        map.current.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

        map.current.on('load', () => {
          setMapLoaded(true);
        });

        map.current.on('error', (e) => {
          console.warn('Map error:', e);
        });
      } catch (error) {
        console.error('Failed to initialize map:', error);
      }
    }

    return () => {
      // Only cleanup markers, not the map
    };
  }, [isOpen]);

  // Resize map when modal opens
  useEffect(() => {
    if (isOpen && map.current) {
      setTimeout(() => {
        map.current?.resize();
      }, 100);
    }
  }, [isOpen]);

  // Update map bounds to fit depot and all markers dynamically
  const updateMapBounds = useCallback(() => {
    if (!map.current) return;

    try {
      let bounds = new mapboxgl.LngLatBounds(DEPOT, DEPOT);
      bounds.extend(DEPOT);

      let hasMarkers = false;

      // Extend by all assigned stop markers
      assignedMarkersRef.current.forEach(marker => {
        bounds.extend(marker.getLngLat());
        hasMarkers = true;
      });

      // Extend by all unassigned order markers
      unassignedMarkersRef.current.forEach(marker => {
        bounds.extend(marker.getLngLat());
        hasMarkers = true;
      });

      if (hasMarkers && !bounds.isEmpty()) {
        map.current.fitBounds(bounds, {
          padding: { top: 80, bottom: 80, left: 80, right: 80 },
          duration: 800,
          maxZoom: 14
        });
      }
    } catch (error) {
      console.error('Error updating map bounds:', error);
    }
  }, [DEPOT]);

  // Render route geometries (driving paths) and assigned stop markers
  const renderRouteGeometryAndAssignedStops = useCallback(async () => {
    if (!map.current || !editedRoute || !Array.isArray(editedRoute.plans)) return;
    const skipDirections = skipDirectionsRef.current;
    skipDirectionsRef.current = false;

    try {
      // Prefetch directions to avoid map/zoom bouncing during async requests
      const fetchedPlans = [];
      for (let planIndex = 0; planIndex < editedRoute.plans.length; planIndex++) {
        const plan = editedRoute.plans[planIndex];
        const stops = (plan.stops || []).map(stop => ({
          ...stop,
          coord: normalizeCoord(stop)
        })).filter(stop => stop.coord.lat && stop.coord.lon);
        if (stops.length > 0) {
          const validStops = stops.map(stop => [stop.coord.lon, stop.coord.lat]);
          // Custom reorder: straight lines only — no Mapbox Directions call
          const coords = skipDirections
            ? validStops
            : await getBatchDirections(validStops, mapboxgl.accessToken, 'driving');
          fetchedPlans.push({ planIndex, plan, stops, coords });
        }
      }

      // Clear existing layers and sources
      const style = map.current.getStyle();
      (style?.layers || [])
        .filter(layer => layer.id.startsWith('route-'))
        .forEach(layer => {
          if (map.current.getLayer(layer.id)) {
            map.current.removeLayer(layer.id);
          }
        });

      Object.keys(style?.sources || {})
        .filter(id => id.startsWith('route-'))
        .forEach(id => {
          if (map.current.getSource(id)) {
            map.current.removeSource(id);
          }
        });

      // Clear assigned markers
      assignedMarkersRef.current.forEach(marker => marker.remove());
      assignedMarkersRef.current = [];

      // Add depot marker
      const depotMarker = new mapboxgl.Marker({ color: '#000000', scale: 1.2 })
        .setLngLat(DEPOT)
        .setPopup(new mapboxgl.Popup().setHTML(`
          <div style="padding: 8px; text-align: center;">
            <h3 style="margin: 0 0 4px 0; color: #FE8403; font-size: 14px; font-weight: 600;">🏢 ${DEPOT_LABEL}</h3>
            <p style="margin: 0; color: #666; font-size: 12px;">Starting Point</p>
          </div>
        `))
        .addTo(map.current);

      assignedMarkersRef.current.push(depotMarker);

      // Draw routes for each plan
      for (let planIndex = 0; planIndex < editedRoute.plans.length; planIndex++) {
        const plan = editedRoute.plans[planIndex];
        const color = plan.color || getDriverColor(plan.driver);
        const stops = (plan.stops || []).map(stop => ({
          ...stop,
          coord: normalizeCoord(stop)
        })).filter(stop => stop.coord.lat && stop.coord.lon);

        if (stops.length === 0) continue;

        const planData = fetchedPlans.find(fp => fp.planIndex === planIndex);
        if (!planData) continue;
        const coords = planData.coords;

        // Add route line
        const srcId = `route-${planIndex}`;
        const lyrId = `route-${planIndex}-line`;

        map.current.addSource(srcId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: coords
            }
          }
        });

        map.current.addLayer({
          id: lyrId,
          type: 'line',
          source: srcId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': color,
            'line-width': 5,
            'line-opacity': 0.9
          }
        });

        // Add markers for stops with driver select popup
        stops.forEach((stop, stopIndex) => {
          const markerLat = stop.coord.lat;
          const markerLon = stop.coord.lon;

          const driverOptions = drivers
            .filter(d => d.status !== false || d.name === plan.driver)
            .map(d =>
              `<option value="${d.name}" ${plan.driver === d.name ? 'selected' : ''}>${d.name}</option>`
            ).join('');

          const popup = new mapboxgl.Popup({
            offset: 24,
            closeButton: true,
            closeOnClick: false,
            maxWidth: '260px'
          }).setHTML(`
            <div style="width: 240px; padding: 12px; padding-right: 32px; box-sizing: border-box;">
              <div style="margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #e5e7eb;">
                <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #111827;">Stop ${stopIndex + 1}</h3>
              </div>
              
              <div style="margin-bottom: 12px;">
                <select id="drv_${stop.order}" style="width: 100%; max-width: 100%; padding: 6px 10px; border: 2px solid #3b82f6; border-radius: 16px; font-size: 13px; font-weight: 600; background: white; color: #111827; outline: none; box-sizing: border-box;">
                  ${driverOptions}
                </select>
              </div>
              
              <div style="font-size: 12px; line-height: 1.4; color: #374151;">
                <div style="margin-bottom: 4px;"><strong>Order #:</strong> ${stop.order || ''}</div>
                ${orderTypeBadgeHtml(stop.type)}
                ${formatPriorityBadgeLabel(stop.priority_del_time)
                  ? `<div style="margin-bottom: 6px;"><span style="display:inline-block;background:#dc2626;color:#fff;padding:2px 8px;border-radius:999px;font-size:11px;font-weight:700;">${formatPriorityBadgeLabel(stop.priority_del_time)}</span></div>`
                  : ''}
                <div style="margin-bottom: 4px;"><strong>Customer:</strong> ${stop.customer || ''}</div>
                <div style="margin-bottom: 4px;"><strong>ETA (arrival):</strong> ${stop.eta_arrival || ''}</div>
                <div style="margin-bottom: 4px;"><strong>Leg time:</strong> ${(stop.leg_minutes || 0).toFixed(1)} min</div>
                <div style="margin-bottom: 4px;"><strong>Cumulative Distance:</strong> ${(stop.cumulative_km || 0).toFixed(2)} km</div>
                <div style="margin-bottom: 4px;"><strong>Handover:</strong> ${stop.handover_time || '5'} min</div>
                <div style="margin-bottom: 8px;"><strong>Depart:</strong> ${stop.depart_time || ''}</div>
              </div>
            </div>
          `);

          const currentPlanDriver = plan.driver;

          popup.on('open', () => {
            setTimeout(() => {
              const selectEl = document.getElementById(`drv_${stop.order}`);
              if (selectEl) {
                if (selectEl._changeHandler) {
                  selectEl.removeEventListener('change', selectEl._changeHandler);
                }

                selectEl._changeHandler = async (e) => {
                  const newDriver = e.target.value;
                  const oldDriver = currentPlanDriver;
                  const orderId = stop.order;

                  if (!newDriver || newDriver === oldDriver) return;

                  popup.remove();

                  const currentRoute = editedRouteRef.current;
                  const allLockedAssignments = buildLockedAssignmentsFromPlans(
                    currentRoute?.plans,
                    { [orderId]: newDriver }
                  );

                  setLockedAssignments(allLockedAssignments);
                  lockedRef.current = allLockedAssignments;

                  const currentDrivers = currentRoute?.plans?.map(p => p.driver).filter(Boolean) || [];
                  const nextDrivers = Array.from(new Set([...currentDrivers, newDriver]));

                  setRouteDrivers(nextDrivers);

                  toast.info(`🔄 Reassigning Order ${orderId} to ${newDriver} and rebuilding routes...`);
                  const ordersForRebuild = collectOrdersFromPlans(currentRoute?.plans, ordersRef.current);
                  await handleRebuildRoutes(nextDrivers, ordersForRebuild);
                };

                selectEl.addEventListener('change', selectEl._changeHandler);
              }
            }, 100);
          });

          const markerElement = document.createElement("div");
          markerElement.style.cssText = `
            background: ${color};
            color: white;
            padding: 6px 12px; 
            border-radius: 16px;
            font-size: 12px;
            font-weight: 500;
            border: none;
            box-shadow: 0 1px 3px rgba(0,0,0,0.2);
            cursor: pointer;
            text-align: center;
            min-width: 24px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            white-space: nowrap;
            gap: 6px;
          `;

          const stopCircle = document.createElement("div");
          stopCircle.style.cssText = `
            background: rgba(255,255,255,0.95);
            color: ${color};
            width: 22px;
            height: 22px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 11px;
            font-weight: 700;
            flex-shrink: 0;
            border: 2px solid rgba(255,255,255,0.3);
          `;
          stopCircle.textContent = stopIndex + 1;

          const orderText = document.createElement("span");
          orderText.style.fontWeight = '600';
          orderText.textContent = stop.order || "?";

          markerElement.appendChild(stopCircle);
          markerElement.appendChild(orderText);

          const marker = new mapboxgl.Marker({ element: markerElement })
            .setLngLat([markerLon, markerLat])
            .setPopup(popup)
            .addTo(map.current);

          assignedMarkersRef.current.push(marker);
        });
      }

      updateMapBounds();
    } catch (error) {
      console.error('Error rendering route geometry:', error);
    }
  }, [editedRoute, getDriverColor, drivers, updateMapBounds]);

  // Render unassigned/available orders
  const renderUnassignedMarkers = useCallback(() => {
    if (!map.current || !editedRoute) return;

    try {
      // Clear existing unassigned markers
      unassignedMarkersRef.current.forEach(marker => marker.remove());
      unassignedMarkersRef.current = [];

      // Render unassigned/available orders
      const assignedOrderIds = new Set();
      editedRoute.plans?.forEach(p => p.stops?.forEach(s => {
        if (s.order) assignedOrderIds.add(toOrderKey(s.order));
      }));

      const selectedOrderSet = new Set(selectedOrders.map(o => toOrderKey(o.order)));
      const unassignedOrders = orders.filter(o =>
        o.order &&
        selectedOrderSet.has(toOrderKey(o.order)) &&
        !assignedOrderIds.has(toOrderKey(o.order)) &&
        o.coord && o.coord.lat && o.coord.lon
      );

      const availableDriversForUnassigned = drivers.filter(d => d.status !== false);

      unassignedOrders.forEach(order => {
        const driverOptions = availableDriversForUnassigned
          .map(d => `<option value="${d.name}">${d.name}</option>`)
          .join('');

        const popup = new mapboxgl.Popup({ offset: 25, closeButton: true, closeOnClick: false })
          .setHTML(`
          <div style="padding: 10px; min-width: 220px;">
            <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 700; color: #1f2937;">Order #${order.order}</h3>
            <div style="margin-bottom: 8px;">
              <select id="drv_unassigned_${order.order}" style="width: 100%; padding: 6px 10px; border: 2px solid #3b82f6; border-radius: 12px; font-size: 13px; font-weight: 600; background: white; color: #111827; outline: none; box-sizing: border-box;">
                <option value="">Select Driver...</option>
                ${driverOptions}
              </select>
            </div>
            <div style="font-size: 12px; color: #4b5563; line-height: 1.4;">
              <div style="margin-bottom: 4px;"><strong>Customer:</strong> ${order.customer || ''}</div>
              <div><strong>Total:</strong> ${order.total || 0} QR</div>
            </div>
          </div>
        `);

        popup.on('open', () => {
          setTimeout(() => {
            const selectEl = document.getElementById(`drv_unassigned_${order.order}`);
            if (selectEl) {
              if (selectEl._changeHandler) {
                selectEl.removeEventListener('change', selectEl._changeHandler);
              }

              selectEl._changeHandler = async (e) => {
                const newDriver = e.target.value;
                if (!newDriver) return;

                popup.remove();

                const currentRoute = editedRouteRef.current;
                const allLockedAssignments = buildLockedAssignmentsFromPlans(
                  currentRoute?.plans,
                  { [order.order]: newDriver }
                );

                const ordersForRebuild = collectOrdersFromPlans(currentRoute?.plans, ordersRef.current);
                if (!ordersForRebuild.some((o) => toOrderKey(o.order) === toOrderKey(order.order))) {
                  ordersForRebuild.push({ ...order, driver: newDriver });
                }

                const updatedSelectedOrders = selectedOrdersRef.current.map(o =>
                  toOrderKey(o.order) === toOrderKey(order.order) ? { ...o, driver: newDriver } : o
                );
                setSelectedOrders(updatedSelectedOrders);
                selectedOrdersRef.current = updatedSelectedOrders;

                setLockedAssignments(allLockedAssignments);
                lockedRef.current = allLockedAssignments;

                const currentDrivers = currentRoute?.plans?.map(p => p.driver).filter(Boolean) || [];
                const nextDrivers = Array.from(new Set([...currentDrivers, newDriver]));
                setRouteDrivers(nextDrivers);

                toast.info(`🔄 Assigning Order ${order.order} to ${newDriver} and rebuilding routes...`);
                await handleRebuildRoutes(nextDrivers, ordersForRebuild);
              };

              selectEl.addEventListener('change', selectEl._changeHandler);
            }
          }, 100);
        });

        const markerElement = document.createElement("div");
        markerElement.style.cssText = `
          background: #9ca3af;
          color: white;
          padding: 6px 12px; 
          border-radius: 16px;
          font-size: 12px;
          font-weight: 500;
          border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          cursor: pointer;
          text-align: center;
          min-width: 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          white-space: nowrap;
          gap: 6px;
        `;

        const packageIcon = document.createElement("span");
        packageIcon.textContent = "📦";
        packageIcon.style.cssText = `
          font-size: 14px;
          flex-shrink: 0;
        `;

        const orderText = document.createElement("span");
        orderText.style.fontWeight = '600';
        orderText.textContent = order.order || "?";

        markerElement.appendChild(packageIcon);
        markerElement.appendChild(orderText);

        const marker = new mapboxgl.Marker({ element: markerElement })
          .setLngLat([parseFloat(order.coord.lon), parseFloat(order.coord.lat)])
          .setPopup(popup)
          .addTo(map.current);

        unassignedMarkersRef.current.push(marker);
      });

      updateMapBounds();
    } catch (error) {
      console.error('Error rendering unassigned markers:', error);
    }
  }, [editedRoute, orders, selectedOrders, drivers, updateMapBounds]);

  // Re-render route geometry when route/drivers changes
  useEffect(() => {
    if (mapLoaded && editedRoute?.plans?.length > 0) {
      renderRouteGeometryAndAssignedStops();
    }
  }, [editedRoute, mapLoaded, renderRouteGeometryAndAssignedStops]);

  // Re-render unassigned markers when orders list or selected list changes
  useEffect(() => {
    if (mapLoaded && editedRoute) {
      renderUnassignedMarkers();
    }
  }, [editedRoute, mapLoaded, orders, selectedOrders, renderUnassignedMarkers]);

  // Fetch unused confirmed orders for this slot/date
  const fetchUnusedOrders = async () => {
    if ((!editedRoute?.slot && !editedRoute?.slotId) || !editedRoute?.date) {
      toast.error('Route slot and date are required');
      return;
    }

    setIsLoadingUnused(true);
    try {
      // Ensure date is in YYYY-MM-DD format for API
      const formattedDate = formatDateOnly(editedRoute.date);
      
      // Get all confirmed orders for this slot/date
      const slotFilter = editedRoute.slot;
      const ordersResponse = await dispatchAPI.getConfirmedOrders(formattedDate, slotFilter);

      if (!ordersResponse.success) {
        toast.error(ordersResponse.msg || 'Failed to fetch confirmed orders');
        return;
      }

      if (!ordersResponse.data) {
        toast.info('No confirmed orders found for this slot');
        return;
      }

      // Get used order IDs from all routes for this slot/date
      const { usedOrderSet } = await dispatchAPI.checkUsedOrdersAndRoute(slotFilter, formattedDate);

      // Also exclude orders already in current modal's orders list
      const currentOrderIds = new Set(orders.map(o => o.order));

      // Filter out orders already in routes OR already in current orders
      const availableOrders = ordersResponse.data
        .filter(order => !usedOrderSet.has(order.order))
        .filter(order => !currentOrderIds.has(order.order))
        .filter(order => order.coordinates && order.coordinates.length === 2)
        .map(order => ({
          id: order.id,
          order: order.order,
          customer: order.customer,
          total: order.total,
          coord: { lon: order.coordinates[0], lat: order.coordinates[1] },
          timeSlot: order.timeSlot,
          deliveryDate: normalizeToYYYYMMDD(order.deliveryDate),
          deliveryStaffUrl: order.deliveryUrl || '',
          priority_del_time: order.priority_del_time || '',
          type: order.type || 'order',
        }));

      // Prepend to main orders list, ensuring no duplicates
      setOrders(prev => {
        const existingOrderIds = new Set(prev.map(o => o.order));
        const newOrders = availableOrders.filter(o => !existingOrderIds.has(o.order));
        return [...newOrders, ...prev];
      });

      setSelectedOrders(prev => {
        const existingSelected = new Set(prev.map(o => o.order));
        const newSelected = availableOrders.filter(o => !existingSelected.has(o.order));
        return [...newSelected, ...prev];
      });

      if (availableOrders.length === 0) {
        toast.info('No new available orders for this slot');
      } else {
        toast.success(`Added ${availableOrders.length} orders to list`);
      }
    } catch (error) {
      console.error('Error fetching unused orders:', error);
      toast.error('Failed to fetch available orders. Please try again.');
    } finally {
      setIsLoadingUnused(false);
    }
  };

  // Handle driver change for existing order (reassignment)
  const handleOrderDriverChange = async (order, newDriver) => {
    if (!newDriver || newDriver === order.driver) return;

    // Update locked assignments
    const newLocked = buildLockedAssignmentsFromPlans(
      editedRouteRef.current?.plans,
      { [order.order]: newDriver }
    );
    setLockedAssignments(newLocked);
    lockedRef.current = newLocked;

    // Update order's driver in state
    setOrders(prev => prev.map(o =>
      o.order === order.order ? { ...o, driver: newDriver } : o
    ));
    setSelectedOrders(prev => prev.map(o =>
      o.order === order.order ? { ...o, driver: newDriver } : o
    ));

    // Add driver if not already in route
    if (!routeDrivers.includes(newDriver)) {
      setRouteDrivers(prev => [...prev, newDriver]);
    }

    setHasUnsavedChanges(true);
    toast.info(`Reassigned order ${order.order} to ${newDriver}. Click "Rebuild routes".`);
  };

  // Toggle order selection (prevent unselecting all)
  const toggleOrderSelection = (order) => {
    setSelectedOrders(prev => {
      const isSelected = prev.some(o => o.order === order.order);
      if (isSelected) {
        // Don't allow unselecting if it's the last selected order
        if (prev.length <= 1) {
          toast.warning('At least one order must remain selected');
          return prev;
        }
        return prev.filter(o => o.order !== order.order);
      }
      return [...prev, order];
    });
  };

  // Select/deselect all orders
  const toggleSelectAll = () => {
    if (selectedOrders.length === orders.length) {
      if (orders.length <= 1) {
        toast.warning('At least one order must remain selected');
        return;
      }
      setSelectedOrders([orders[0]]);
    } else {
      setSelectedOrders([...orders]);
    }
  };

  // Rebuild routes
  const handleRebuildRoutes = async (driversOverride, ordersOverride = null) => {
    if (!editedRoute) return;

    // Use driversOverride if provided, otherwise use routeDrivers state
    const driversToUse = Array.from(new Set((driversOverride && driversOverride.length ? driversOverride : routeDrivers)));
    if (!driversToUse.length) {
      toast.error('Please select at least one driver.');
      return;
    }

    const sourceOrders = ordersOverride || selectedOrders;
    const transformedOrders = sourceOrders
      .map(order => {
        const coord = normalizeCoord(order);
        if (!coord.lat || !coord.lon) return null;

        return {
          id: order.orderId || order.id,
          order: order.order,
          customer: order.customer,
          total: order.total,
          coordinates: [coord.lon, coord.lat],
          timeSlot: editedRoute.slot,
          deliveryDate: editedRoute.date,
          deliveryStaffUrl: order.deliveryStaffUrl || '',
          priority_del_time: order.priority_del_time || '',
          type: order.type || 'order',
          driver: lockedRef.current?.[toOrderKey(order.order)] || undefined
        };
      })
      .filter(order => order !== null && order.coordinates && order.coordinates.length === 2);

    if (transformedOrders.length === 0) {
      toast.error('No valid orders with coordinates to rebuild');
      return;
    }

    setLoading(true);

    try {
      const driversPayload = driversToUse.map(name => {
        const planInfo = editedRoute.plans?.find(p => p.driver === name);
        const driverInfo = drivers.find(d => d.name === name);

        return {
          name: name,
          id: planInfo?.driverId || driverInfo?._id || null,
          color: planInfo?.color || driverInfo?.color || getDriverColor(name)
        };
      });

      const selectedOrderIds = new Set(transformedOrders.map(o => toOrderKey(o.order)));
      const filteredLocked = {};
      Object.entries(lockedRef.current || {}).forEach(([orderId, driver]) => {
        const key = toOrderKey(orderId);
        if (selectedOrderIds.has(key)) {
          filteredLocked[key] = driver;
        }
      });

      const response = await dispatchAPI.planRoutes({
        slot: editedRoute.slot,
        slotId: editedRoute.slotId,
        date: editedRoute.date,
        serviceMin: 5,
        drivers: driversPayload,
        orders: transformedOrders,
        lockedAssignments: filteredLocked
      });

      if (response.success && response.data?.plans) {
        // Build orderIdMap from original route plans
        const orderIdMap = {};
        route.plans?.forEach(plan => {
          plan.stops?.forEach(stop => {
            if (stop.order && stop.orderId) {
              orderIdMap[toOrderKey(stop.order)] = stop.orderId;
            }
          });
        });

        // Also include order IDs from current selectedOrders (for newly fetched orders)
        selectedOrders.forEach(order => {
          if (order.order && order.id && !orderIdMap[toOrderKey(order.order)]) {
            orderIdMap[toOrderKey(order.order)] = order.id;
          }
        });

        const newPlans = response.data.plans.map(driverPlan => ({
          ...driverPlan,
          stops: (driverPlan.stops || []).map(stop => {
            const coord = normalizeCoord(stop);
            const originalOrderId = orderIdMap[toOrderKey(stop.order)] || stop.orderId;
            return {
              ...stop,
              orderId: originalOrderId,
              coord
            };
          })
        }));

        setEditedRoute(prev => ({ ...prev, plans: newPlans }));
        setHasUnsavedChanges(true);

        // Update orders list
        const newOrders = [];
        newPlans.forEach(plan => {
          plan.stops?.forEach(stop => {
            newOrders.push(buildOrderFromStop(stop, plan.driver));
          });
        });
        setOrders(prev => {
          const existingMap = new Map(prev.map(o => [o.order, o]));

          newOrders.forEach(o => {
            existingMap.set(o.order, o);
          });

          return Array.from(existingMap.values());
        });

        setSelectedOrders(prevSelected => {
          const selectedMap = new Map(prevSelected.map(o => [o.order, o]));

          newOrders.forEach(o => {
            selectedMap.set(o.order, o);
          });

          return Array.from(selectedMap.values());
        });


        // Update routeDrivers to match the new plans
        const updatedDrivers = newPlans.map(p => p.driver).filter(Boolean);
        setRouteDrivers(updatedDrivers);

        if (response.data?.rejected_orders > 0) {
          toast.warning(`${response.data.rejected_orders} orders could not be assigned due to capacity constraints.`);
        } else {
          toast.success('Routes rebuilt successfully!');
        }
      } else {
        toast.error(response.msg || 'Failed to rebuild routes');
      }
    } catch (error) {
      console.error('Error rebuilding routes:', error);
      toast.error('Failed to rebuild routes');
    } finally {
      setLoading(false);
    }
  };

  // Handle driver change in totals table - swap drivers like DispatchPlanner
  const handleDriverChange = async (oldDriver, newDriver, rowIndex) => {
    if (!newDriver || newDriver === oldDriver || !editedRoute?.plans) return;

    try {
      setLoading(true);

      const updatedPlans = [...editedRoute.plans];
      const newDriverIndex = updatedPlans.findIndex(plan => plan.driver === newDriver);

      // Get driver IDs from drivers prop
      const newDriverInfo = drivers.find(d => d.name === newDriver);
      const oldDriverInfo = drivers.find(d => d.name === oldDriver);

      if (updatedPlans[rowIndex]) {
        updatedPlans[rowIndex] = {
          ...updatedPlans[rowIndex],
          driver: newDriver,
          driverId: newDriverInfo?._id || updatedPlans[rowIndex].driverId,
          color: getDriverColor(newDriver)
        };
      }

      // If new driver already has a plan, swap the drivers
      if (newDriverIndex !== -1 && newDriverIndex !== rowIndex) {
        updatedPlans[newDriverIndex] = {
          ...updatedPlans[newDriverIndex],
          driver: oldDriver,
          driverId: oldDriverInfo?._id || updatedPlans[newDriverIndex].driverId,
          color: getDriverColor(oldDriver)
        };
      }

      setEditedRoute(prev => ({ ...prev, plans: updatedPlans }));

      // Update routeDrivers
      const updatedDrivers = updatedPlans.map(p => p.driver).filter(Boolean);
      setRouteDrivers(updatedDrivers);

      setHasUnsavedChanges(true);
      toast.success(`Swapped ${oldDriver} with ${newDriver}`);
    } catch (error) {
      console.error('Error swapping drivers:', error);
      toast.error('Failed to swap drivers');
    } finally {
      setLoading(false);
    }
  };

  // Custom stop order — local only, no Mapbox Directions / rebuild
  const handleCustomStopMove = (driverName, fromIndex, toIndex) => {
    const from = Number(fromIndex);
    const to = Number(toIndex);
    if (!driverName || Number.isNaN(from) || Number.isNaN(to) || from === to) return;
    if (!editedRoute?.plans) return;

    const plan = editedRoute.plans.find((p) => p.driver === driverName);
    if (!plan?.stops?.length) return;
    if (from < 0 || to < 0 || from >= plan.stops.length || to >= plan.stops.length) return;

    const updatedPlans = editedRoute.plans.map((p) => {
      if (p.driver !== driverName) return p;
      const stops = [...(p.stops || [])];
      const [moved] = stops.splice(from, 1);
      stops.splice(to, 0, moved);
      const stopCoords = stops
        .map((s) => normalizeCoord(s))
        .filter((c) => c.lat != null && c.lon != null)
        .map((c) => [c.lon, c.lat]);
      const waypoints = [DEPOT, ...stopCoords];
      return {
        ...p,
        stops,
        coordinates: waypoints,
        route_coordinates: waypoints,
      };
    });

    skipDirectionsRef.current = true;
    setEditedRoute((prev) => ({ ...prev, plans: updatedPlans }));
    setHasUnsavedChanges(true);
  };

  // Handle close
  const handleClose = () => {
    closeModal();
  };

  const closeModal = () => {
    if (map.current) {
      map.current.remove();
      map.current = null;
    }
    setMapLoaded(false);
    onClose();
  };

  // Handle save
  const handleSave = async () => {
    if (!editedRoute) return;

    setLoading(true);
    const success = await onSave({
      ...editedRoute,
      slotId: editedRoute.slotId || route.slotId
    });
    setLoading(false);

    if (success) {
      setHasUnsavedChanges(false);
      closeModal();
    }
  };

  // Calculate totals for each plan
  const planTotals = useMemo(() => {
    if (!editedRoute?.plans) return [];

    return editedRoute.plans.map(plan => ({
      driver: plan.driver,
      color: plan.color || getDriverColor(plan.driver),
      distance: plan.distance_km?.toFixed(2) || '0',
      time: plan.total_hhmm || '0:00',
      returnEta: plan.return_eta || '-',
      load: plan.load || 'Normal',
      timeCAPViolation: plan.time_cap_violation || false,
      orders: plan.stops?.length || 0
    }));
  }, [editedRoute, getDriverColor]);

  // Generate grouped messages for copy/share
  const groupedMessages = useMemo(() => {
    if (!editedRoute?.plans) return {};

    const m = {};
    editedRoute.plans.forEach(p => {
      const driverHeader = [
        '______________________________',
        `Delivery Staff : ${p.driver}`,
        '______________________________'
      ].join('\n');

      const orderLines = (p.stops || []).filter(s => s && s.coord && s.coord.lat != null && s.coord.lon != null).map((s, idx) => [
        `🔢 Stop ${idx + 1}`,
        `📦 ORDER #${s.order || ''}`,
        ...(getOrderTypeBadge(s.type) ? [getOrderTypeBadge(s.type).kind === 'return' ? `↩️ ${getOrderTypeBadge(s.type).label}` : `🔁 ${getOrderTypeBadge(s.type).label}`] : []),
        ...(formatPriorityBadgeLabel(s.priority_del_time) ? [`🔴 ${formatPriorityBadgeLabel(s.priority_del_time)}`] : []),
        '-------------------------------',
        `Customer: ${s.customer || ''}`,
        `Order Total: ${s.total || ''} QR`,
        `Customer location: https://maps.google.com/?q=${s.coord.lat},${s.coord.lon}`,
        `Confirm Delivery Status: ${s.deliveryStaffUrl || ''}`,
        '______________________________'
      ].join('\n'));

      let routeOverviewUrl = `${API_CONFIG.ADMIN_BASE_URL}/route-overview?driver=${encodeURIComponent(p.driver)}`;

      const currentRouteId = route?._id || route?.route_id;
      if (currentRouteId) {
        routeOverviewUrl += `&routeId=${currentRouteId}`;
      }

      const routeOverviewLine = [
        '🗺️ ROUTE OVERVIEW',
        '-------------------------------',
        `Route overview: ${routeOverviewUrl}`,
        '______________________________'
      ].join('\n');

      const driverOutput = [driverHeader, ...orderLines, routeOverviewLine].join('\n');
      m[p.driver] = driverOutput;
    });
    return m;
  }, [editedRoute, route]);

  const checkBeforeShare = (txt, driverName, type) => {
    if (hasUnsavedChanges) {
      setPendingAction({ txt, driverName, type });
      setShowUnsavedModal(true);
      return;
    }

    performShareAction(txt, driverName, type);
  };

  // Copy to clipboard
  // Perform actual share action
  const performShareAction = (txt, driverName, type) => {
    if (type === 'copy') {
      navigator.clipboard.writeText(txt)
        .then(() => toast.success('Copied to clipboard!'))
        .catch(() => toast.error('Failed to copy to clipboard'));
    } else if (type === 'whatsapp') {
      const encodedText = encodeURIComponent(txt);
      const driver = drivers.find(d => d.name === driverName);
      const phone = driver?.phoneNumber?.replace(/[^\d]/g, '');
      const waUrl = phone ? `https://wa.me/${phone}?text=${encodedText}` : `https://wa.me/?text=${encodedText}`;
      window.open(waUrl, '_blank');
    }
  };

  const proceedWithShare = async () => {
    if (!pendingAction) return;

    try {
      setLoading(true);

      const routeId = editedRoute._id || editedRoute.id || route._id || route.id;
      const saveResult = await dispatchAPI.updateRouteInHistory(
        routeId,
        editedRoute.plans,
        editedRoute.slot,
        editedRoute.date,
        editedRoute.slotId || route.slotId
      );

      if (!saveResult?.success) {
        toast.error(getApiErrorMessage(saveResult, 'Failed to save route'));
        return;
      }

      setHasUnsavedChanges(false);
      performShareAction(pendingAction.txt, pendingAction.driverName, pendingAction.type);
      setShowUnsavedModal(false);
      setPendingAction(null);
      toast.success('Route saved and ready to share!');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save route'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !route) return null;

  return (
    <div className="dispatch-edit-modal-overlay">
      <div className="dispatch-edit-modal">
        {/* Header */}
        <div className="dispatch-edit-modal-header">
          <div>
            <h2>Route ID: {route.route_id || route._id || 'Unknown'}</h2>
            <div className="dispatch-edit-modal-badges">
              <span className="dispatch-edit-badge slot">{editedRoute?.slot || route.slot}</span>
              <span className="dispatch-edit-badge date">{formatDateOnly(route.date)}</span>
              <span className="dispatch-edit-badge drivers">{routeDrivers.length} drivers</span>
              <span className="dispatch-edit-badge orders">{orders.length} orders</span>
            </div>
            <div className="dispatch-edit-modal-last-edited">
              Last edited: {route.updatedAt ? new Date(route.updatedAt).toLocaleString() : '-'}
            </div>
          </div>
          <button className="dispatch-edit-modal-close-btn" onClick={handleClose}>×</button>
        </div>

        {/* Content */}
        <div className="dispatch-edit-modal-content">
          {/* Left Panel */}
          <div className="dispatch-edit-left-panel">
            <div className="dispatch-edit-left-scroll">
              {/* Route Orders */}
              <div className="dispatch-edit-route-orders-card">
                <div className="dispatch-edit-route-orders-header">
                  <span>Route orders ({orders.length})</span>
                  {orders.length > 0 && (
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedOrders.length === orders.length && orders.length > 0}
                        onChange={toggleSelectAll}
                      />
                      {selectedOrders.length === orders.length ? 'All Selected' : 'Select All'}
                    </label>
                  )}
                </div>

                <div className="dispatch-edit-route-orders-list">
                  {orders.map((order, idx) => {
                    const isSelected = selectedOrders.some(o => o.order === order.order);
                    return (
                      <div
                        key={order.order || idx}
                        className={`dispatch-edit-order-item ${isSelected ? 'selected' : ''}`}
                        style={{ backgroundColor: order.driver ? getDriverColor(order.driver) + '20' : '#f9fafb' }}
                      >
                        <div className="dispatch-edit-order-item-header">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOrderSelection(order)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <span className="dispatch-edit-order-item-number">
                            #{order.order}
                            {getOrderTypeBadge(order.type) && (
                              <span className={`dispatch-type-badge ${getOrderTypeBadge(order.type).kind}`}>
                                {getOrderTypeBadge(order.type).label}
                              </span>
                            )}
                            {formatPriorityBadgeLabel(order.priority_del_time) && (
                              <span className="dispatch-priority-badge">
                                {formatPriorityBadgeLabel(order.priority_del_time)}
                              </span>
                            )}
                          </span>
                          <span
                            style={{
                              marginLeft: 'auto',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: '#374151',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {order.customer || '—'}
                          </span>
                        </div>
                        <div className="dispatch-edit-order-item-details">
                          {order.total} QR
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Fetch Available Orders Button */}
              <button
                onClick={fetchUnusedOrders}
                disabled={isLoadingUnused}
                style={{
                  width: '100%',
                  padding: '10px 16px',
                  marginBottom: '12px',
                  background: '#f0f9ff',
                  border: '1px dashed #3b82f6',
                  borderRadius: '8px',
                  color: '#1d4ed8',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: isLoadingUnused ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {isLoadingUnused ? 'Loading...' : 'Fetch Available Orders'}
              </button>

              {/* Driver Assignments */}
              <div className="dispatch-edit-driver-assignments">
                <div className="dispatch-edit-driver-assignments-label">Drivers</div>
                <div className="dispatch-edit-driver-assignments-tags">
                  {routeDrivers.map((driverName, idx) => (
                    <span
                      key={idx}
                      className="dispatch-edit-driver-tag"
                      style={{ backgroundColor: getDriverColor(driverName) }}
                    >
                      {driverName}
                    </span>
                  ))}
                </div>
              </div>

              {/* Expandable Message Section like DispatchPlanner */}
              <div className="dispatch-edit-messages-section-inline">
                {Object.keys(groupedMessages).length === 0 ? (
                  <div className="dispatch-edit-no-messages">
                    Rebuild routes to see outputs...
                  </div>
                ) : (
                  <div style={{ marginBottom: '8px', maxHeight: '300px', overflowY: 'auto' }}>
                    {Object.entries(groupedMessages).map(([driverName, message]) => {
                      const color = getDriverColor(driverName);
                      return (
                        <details key={driverName} className="dispatch-edit-message-card" style={{ margin: '4px 0', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <summary style={{ padding: '6px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: color }}></div>
                              <b>{driverName.toUpperCase()} <span style={{ fontSize: '12px', color: '#6b7280' }}>({editedRoute?.plans?.find(p => p.driver === driverName)?.stops?.length || 0})</span></b>
                            </div>
                            <div style={{ display: 'flex', gap: 8 }} onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => checkBeforeShare(message, driverName, 'copy')}
                                title="Copy"
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Copy size={16} style={{ color: '#6b7280' }} />
                              </button>
                              <button
                                onClick={() => checkBeforeShare(message, driverName, 'whatsapp')}
                                title={`WhatsApp ${driverName}`}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <MessageCircle size={16} style={{ color: '#25D366' }} />
                              </button>
                            </div>
                          </summary>
                          <div style={{ padding: '8px 12px' }}>
                            <div style={{ background: '#fafafa', borderRadius: '6px', padding: '8px' }}>
                              <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{message}</pre>
                            </div>
                          </div>
                        </details>
                      );
                    })}
                  </div>
                )}
              </div>

              {editedRoute?.plans?.length > 0 && (
                <div className="dispatch-edit-custom-route">
                  <div className="dispatch-edit-custom-route-header">
                    <h4>Custom Route Setup</h4>
                    <p>Change stop order without rebuild / Mapbox.</p>
                  </div>
                  {editedRoute.plans.map((plan) => {
                    const stops = plan.stops || [];
                    if (!stops.length) return null;
                    const color = plan.color || getDriverColor(plan.driver);
                    return (
                      <div key={plan.driver} className="dispatch-edit-custom-route-driver">
                        <div className="dispatch-edit-custom-route-driver-title">
                          <span className="dispatch-edit-custom-route-dot" style={{ background: color }} />
                          <strong>{plan.driver}</strong>
                          <span className="dispatch-edit-custom-route-count">{stops.length} stops</span>
                        </div>
                        <div className="dispatch-edit-custom-route-stops">
                          {stops.map((stop, idx) => (
                            <div key={`${plan.driver}-${stop.orderId || stop.order}-${idx}`} className="dispatch-edit-custom-route-stop">
                              <select
                                className="dispatch-edit-custom-route-pos"
                                value={idx + 1}
                                aria-label={`Position for order ${stop.order}`}
                                onChange={(e) => handleCustomStopMove(plan.driver, idx, Number(e.target.value) - 1)}
                              >
                                {stops.map((_, i) => (
                                  <option key={i} value={i + 1}>{i + 1}</option>
                                ))}
                              </select>
                              <div className="dispatch-edit-custom-route-stop-info">
                                <span className="dispatch-edit-custom-route-order">
                                  #{stop.order}
                                  {getOrderTypeBadge(stop.type) && (
                                    <span className={`dispatch-type-badge ${getOrderTypeBadge(stop.type).kind}`}>
                                      {getOrderTypeBadge(stop.type).label}
                                    </span>
                                  )}
                                  {formatPriorityBadgeLabel(stop.priority_del_time) && (
                                    <span className="dispatch-priority-badge">
                                      {formatPriorityBadgeLabel(stop.priority_del_time)}
                                    </span>
                                  )}
                                </span>
                                <span className="dispatch-edit-custom-route-customer">{stop.customer || '—'}</span>
                              </div>
                              <div className="dispatch-edit-custom-route-move-btns">
                                <button
                                  type="button"
                                  title="Move up"
                                  disabled={idx === 0}
                                  onClick={() => handleCustomStopMove(plan.driver, idx, idx - 1)}
                                >
                                  <ChevronUp size={16} />
                                </button>
                                <button
                                  type="button"
                                  title="Move down"
                                  disabled={idx === stops.length - 1}
                                  onClick={() => handleCustomStopMove(plan.driver, idx, idx + 1)}
                                >
                                  <ChevronDown size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Rebuild Button */}
              <button
                className="dispatch-edit-rebuild-routes-btn"
                onClick={handleRebuildRoutes}
                disabled={loading || selectedOrders.length === 0}
              >
                <RotateCcw size={18} />
                {loading ? 'Rebuilding...' : 'Rebuild routes'}
              </button>
            </div>
          </div>

          {/* Right Panel */}
          <div className="dispatch-edit-right-panel">
            {/* Map */}
            <div className="dispatch-edit-map-container">
              <div ref={mapContainer} className="dispatch-edit-map" />

              {/* Map Loading Overlay */}
              {loading && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  borderRadius: '8px'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #e5e7eb',
                    borderTopColor: '#FE8403',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div style={{
                    marginTop: '12px',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>Rebuilding routes...</div>
                </div>
              )}
            </div>

            {/* Driver Totals */}
            <div className="dispatch-edit-driver-totals-section">
              <div className="dispatch-edit-driver-totals-header">
                <span>Driver Totals (drive + 5 min per stop)</span>
                <span>Start: {route.slot?.split('-')[0]?.trim() || '-'} | Date: {formatDateOnly(route.date)}</span>
              </div>

              {isMobile ? (
                /* Mobile: Card layout like DispatchPlanner */
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', padding: '8px', boxSizing: 'border-box' }}>
                  {planTotals.length > 0 ? planTotals.map((total, idx) => (
                    <div key={idx} style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px'
                    }}>
                      {/* Driver Header */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', fontSize: '16px' }}>
                          <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', background: total.color }} />
                          <select
                            value={total.driver}
                            onChange={(e) => handleDriverChange(total.driver, e.target.value, idx)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#1f2937',
                              cursor: 'pointer',
                              outline: 'none',
                              padding: '2px 4px',
                              borderRadius: '4px',
                              minWidth: '80px'
                            }}
                          >
                            {drivers.filter(d => d.status !== false || d.name === total.driver).map(d => (
                              <option key={d.name} value={d.name}>{d.name}</option>
                            ))}
                          </select>
                        </div>
                        <span style={{
                          background: total.load === 'Normal' ? '#ecfdf5' : '#fef2f2',
                          color: total.load === 'Normal' ? '#065f46' : '#991b1b',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '13px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: total.load === 'Normal' ? '#10b981' : '#ef4444' }} />
                          {total.load}
                        </span>
                      </div>

                      {/* Stats Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '6px',
                        fontSize: '13px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>Distance:</span>
                          <span style={{ fontWeight: '500' }}>{total.distance} km</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>Orders:</span>
                          <span style={{ fontWeight: '500' }}>{total.orders}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>Time:</span>
                          <span style={{ fontWeight: '500' }}>{total.time}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#6b7280' }}>Return:</span>
                          <span style={{ fontWeight: '500' }}>{total.returnEta}</span>
                        </div>
                      </div>
                    </div>
                  )) : (
                    <div style={{
                      background: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      color: '#6b7280',
                      fontSize: '14px'
                    }}>
                      No routes built yet. Rebuild routes to see driver totals.
                    </div>
                  )}
                </div>
              ) : (
                /* Desktop: Table layout */
                <table className="dispatch-edit-driver-totals-table">
                  <thead>
                    <tr>
                      <th>Driver</th>
                      <th>Total Dist.</th>
                      <th>Total Time</th>
                      <th>Return ETA</th>
                      <th>Driver Load</th>
                      <th># Orders</th>
                    </tr>
                  </thead>
                  <tbody>
                    {planTotals.map((total, idx) => (
                      <tr key={idx}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span
                              style={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: total.color
                              }}
                            />
                            <select
                              className="dispatch-edit-driver-select"
                              value={total.driver}
                              onChange={(e) => handleDriverChange(total.driver, e.target.value, idx)}
                            >
                              {drivers.filter(d => d.status !== false || d.name === total.driver).map(driverObj => (
                                <option key={driverObj.name} value={driverObj.name}>{driverObj.name}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td>{total.distance} km</td>
                        <td>{total.time}</td>
                        <td>{total.returnEta}</td>
                        <td className={total.load === 'Normal' ? 'dispatch-edit-load-normal' : 'dispatch-edit-load-overloaded'}>
                          ● {total.load}
                        </td>
                        <td>{total.orders}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="dispatch-edit-modal-footer">
          <button className="dispatch-edit-cancel-btn" onClick={handleClose}>Cancel</button>
          <button className="dispatch-edit-save-btn" onClick={handleSave} disabled={loading}>
            Save Changes
          </button>
        </div>
      </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedModal && (
        <div className="dispatch-modal-overlay">
          <div className="dispatch-modal-box">
            <div className="dispatch-modal-header">
              <h2>Unsaved Changes</h2>
              <button className="dispatch-modal-close-btn" onClick={() => { setShowUnsavedModal(false); setPendingAction(null); }}>
                <X size={20} />
              </button>
            </div>
            <div className="dispatch-modal-content">
              <p>You have unsaved changes. Would you like to save before sharing?</p>
            </div>
            <div className="dispatch-modal-actions">
              <button
                className="dispatch-modal-cancel-btn"
                onClick={() => { setShowUnsavedModal(false); setPendingAction(null); }}
              >
                Cancel
              </button>
              <button
                className="dispatch-modal-save-btn"
                onClick={proceedWithShare}
              >
                Save & Share
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchEditModal;
